import { useCallback, useMemo, useState } from "react";
import { AccountMappingRow } from "../api/fetchAccountMappings/types";
import { LedgerAccount } from "../api/fetchLedgerAccounts/types";
import { LedgerAccountRow } from "./useAccountMappingSection";
import { isLikelySubtypeMatch } from "../config/accountMappingDisplay";

/**
 * Where a QuickBooks account sits relative to the CertainPath account being
 * filled. Ordered by how likely the member is to want it.
 */
export type PickerTier = "likely" | "unplaced" | "elsewhere";

export const PICKER_TIER_LABELS: Record<PickerTier, string> = {
  likely: "Likely matches",
  unplaced: "Not placed anywhere yet",
  elsewhere: "Currently in another account",
};

const TIER_ORDER: PickerTier[] = ["likely", "unplaced", "elsewhere"];

export interface PickerCandidate {
  account: AccountMappingRow;
  tier: PickerTier;
}

interface UseAccountPickerDrawerParams {
  accounts: AccountMappingRow[];
  ledgerAccounts: LedgerAccount[];
  rows: LedgerAccountRow[];
  assignAccounts: (qboAccountIds: string[], ledgerAccountCode: string) => void;
}

export const useAccountPickerDrawer = ({
  accounts,
  ledgerAccounts,
  rows,
  assignAccounts,
}: UseAccountPickerDrawerParams) => {
  const [targetCode, setTargetCode] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");

  const open = useCallback((ledgerAccountCode: string) => {
    setTargetCode(ledgerAccountCode);
    setSelectedIds(new Set());
    setQuery("");
  }, []);

  const close = useCallback(() => {
    setTargetCode(null);
    setSelectedIds(new Set());
    setQuery("");
  }, []);

  const target = useMemo(
    () =>
      ledgerAccounts.find(
        (ledgerAccount) => ledgerAccount.code === targetCode,
      ) ?? null,
    [ledgerAccounts, targetCode],
  );

  /** What is already in the account being filled, shown above the pick list. */
  const accountsInTarget = useMemo(
    () =>
      targetCode === null
        ? []
        : accounts.filter(
            (account) => account.mapping?.ledgerAccountCode === targetCode,
          ),
    [accounts, targetCode],
  );

  /** Name, account number or subtype — the three things a member types. */
  const matchesSearch = useCallback(
    (account: AccountMappingRow): boolean => {
      const search = query.trim().toLowerCase();

      return (
        search === "" ||
        account.name.toLowerCase().includes(search) ||
        (account.accountNumber ?? "").toLowerCase().includes(search) ||
        (account.accountSubType ?? "").toLowerCase().includes(search)
      );
    },
    [query],
  );

  const candidates = useMemo((): PickerCandidate[] => {
    if (target === null) {
      return [];
    }

    const tierOf = (account: AccountMappingRow): PickerTier => {
      if (
        isLikelySubtypeMatch(account.accountSubType, target.qboSubtypeMatches)
      ) {
        return "likely";
      }

      return account.mapping === null ? "unplaced" : "elsewhere";
    };

    const eligible = accounts
      .filter((account) => account.mapping?.ledgerAccountCode !== target.code)
      .filter(matchesSearch)
      .map((account) => ({ account, tier: tierOf(account) }));

    // Ordered by tier, then left in the order the API returned them — account
    // number, then name, the same order the member's own QuickBooks list uses.
    const tiered = TIER_ORDER.flatMap((tier) =>
      eligible.filter((candidate) => candidate.tier === tier),
    );

    // Retired accounts sink below everything, across tiers rather than within
    // them — a retired account is not a "likely match" whatever its subtype, and
    // burying it inside a tier would put it above live accounts in later tiers.
    return [
      ...tiered.filter((candidate) => candidate.account.isActive),
      ...tiered.filter((candidate) => !candidate.account.isActive),
    ];
  }, [accounts, target, matchesSearch]);

  const toggleSelected = useCallback((qboAccountId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);

      if (next.has(qboAccountId)) {
        next.delete(qboAccountId);
      } else {
        next.add(qboAccountId);
      }

      return next;
    });
  }, []);

  const selectAllInTier = useCallback(
    (tier: PickerTier) => {
      setSelectedIds((current) => {
        const next = new Set(current);

        candidates
          .filter((candidate) => candidate.tier === tier)
          .forEach((candidate) => next.add(candidate.account.qboAccountId));

        return next;
      });
    },
    [candidates],
  );

  const commit = useCallback(() => {
    if (targetCode === null || selectedIds.size === 0) {
      return;
    }

    assignAccounts([...selectedIds], targetCode);
    setSelectedIds(new Set());
    setQuery("");
  }, [assignAccounts, selectedIds, targetCode]);

  /**
   * The reason the drawer beats an inline panel: finish one account, go
   * straight to the next that needs work without returning to the list.
   * Required gaps first, then anything with unaccepted suggestions.
   */
  const nextNeedy = useMemo(() => {
    const ordered = [
      ...rows.filter((row) => row.state === "gap"),
      ...rows.filter((row) => row.state === "pending"),
    ];

    return (
      ordered.find((row) => row.ledgerAccount.code !== targetCode)
        ?.ledgerAccount ?? null
    );
  }, [rows, targetCode]);

  return {
    target,
    accountsInTarget,
    candidates,
    query,
    setQuery,
    selectedIds,
    selectedCount: selectedIds.size,
    toggleSelected,
    selectAllInTier,
    commit,
    nextNeedy,
    open,
    close,
    switchTarget: open,
  };
};
